/* eslint-disable node/no-unsupported-features/node-builtins */
import React, {ErrorInfo} from 'react';
import {createRoot} from 'react-dom/client';
import * as Sentry from '@sentry/react';
import {ErrorBoundary} from 'react-error-boundary';
import {AssetType, PageStateManager, store, Taopix as MotherShip} from '@taopix/taopix-core';
import ErrorFallback from './Components/ErrorBoundary/ErrorFallback';
import Taopix from './Components/Taopix/Taopix';
import '@taopix/taopix-design-system/dist/taopix.css';
import './index.css';
import {
  AssetMetaInterface,
  AutoFillArgs,
  AutoFillResponse,
  ReducerState,
} from '@taopix/taopix-core/dist/types/types';

window.history.pushState = (f =>
  function pushState() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line prefer-rest-params
    const ret = f.apply(this, arguments);
    window.dispatchEvent(new Event('pushstate'));
    window.dispatchEvent(new Event('locationchange'));
    return ret;
  })(window.history.pushState);

window.addEventListener('locationchange' as any, (event: PopStateEvent) => {
  setTimeout(() => {
    const params = new URLSearchParams(document.location.search);
    const payload = params.get('ref');
    if (payload) {
      // collect any user assets we have
      const assets = Object.values<AssetMetaInterface>(store.getState().project.assets).filter(
        (a: AssetMetaInterface) => a.assetType === AssetType.User
      );

      const settings = new URLSearchParams(atob(payload));

      PubSub.publish('SWITCH_COLLECTION', {
        ref: String(settings.get('ref')),
        theme: String(settings.get('theme')),
        assets: [...assets],
        onAfterLoad: (stateManager: PageStateManager) => {
          if (stateManager.getAssetsByType(AssetType.User).length === 0) {
            return false;
          }
          stateManager
            .call<AutoFillArgs, ReducerState & AutoFillResponse>('autofill', {
              save: true,
              showLoading: false,
              sortOrder: 'dt',
              sortDirection: 'asc',
              includeCovers: true,
              coverItems: [],
              fromCreation: false,
              createPreviewThumbnail: true,
              checkImageUsage: true,
            })
            .catch(error => {
              // console.error(error);
            });
        },
      });
    }
  }, 0);
});

Sentry.init({
  dsn: 'https://492c16197a7ddcd7b3009eec3b6c0857@o212614.ingest.sentry.io/4506030224506880',
  integrations: [Sentry.replayCanvasIntegration(), Sentry.browserTracingIntegration()],
  // Performance Monitoring
  tracesSampleRate: 0.1, // Capture 100% of the transactions, reduce in production!
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  beforeSend: (event, hint) => {
    // if we have a booted version of the core, add in the core info into the event extra data
    if (window?.Taopix?.Core) {
      // grab the core info
      const info = window.Taopix.Core.get('info');

      // append it to the event extra data
      event.extra = {...event.extra, info: info};

      // append the projectRef & core version to the tags
      event.tags = {
        ...event.tags,
        projectRef: info.projectRef || null,
        coreVersion: info.version,
      };
    }
    return event;
  },
  ignoreErrors: [/^Request failed with status code (403|401)$/, 'AxiosError: Network Error'],
});

// Set the colour of the mobile browser surround (after a delay to give the theme a chance to load)
setTimeout(() => {
  // Get the colour of the body element
  const bodyEl = document.getElementsByTagName('body')[0];
  const bodyBg = bodyEl ? window.getComputedStyle(bodyEl).backgroundColor : 'rgb(255,0,0)';
  // Add it to the theme-color meta tag
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bodyBg);
}, 500);

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);

const onError = (error: Error, info: ErrorInfo) => {
  console.error(error.message);
};

const core = new MotherShip({});

root.render(
  <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
    <Taopix core={core} />
  </ErrorBoundary>
);
